import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Video, Plus, PhoneCall, Copy, Users } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminVideoCalls — trainer schedules 1-on-1 OR group video calls.
 * Multiple clients can be selected at once; everyone joins the same
 * Jitsi room via their dashboard. Jitsi Meet is 100 % free.
 */
export default function AdminVideoCalls() {
  const [calls, setCalls] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ participant_ids: [], topic: '', scheduled_for: '' });
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = () => api.get('/video-calls/mine').then(r => setCalls(r.data)).catch(() => setCalls([]));
  useEffect(() => { load(); api.get('/admin/clients').then(r => setClients(r.data)); }, []);

  const toggleClient = (cid) => {
    setForm(f => ({
      ...f,
      participant_ids: f.participant_ids.includes(cid)
        ? f.participant_ids.filter(x => x !== cid)
        : [...f.participant_ids, cid],
    }));
  };
  const selectAll = () => setForm(f => ({ ...f, participant_ids: filtered.map(c => c.id) }));
  const selectNone = () => setForm(f => ({ ...f, participant_ids: [] }));

  const createCall = async () => {
    if (form.participant_ids.length === 0) { toast.error('Pick at least one client'); return; }
    try {
      const { data: newCall } = await api.post('/admin/video-calls', form);
      setOpen(false); setForm({ participant_ids: [], topic: '', scheduled_for: '' }); load();
      toast.success('Call room created');
      navigate(`/call/${newCall.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create'); }
  };

  const copyLink = async (c) => {
    const url = `${window.location.origin}/call/${c.id}`;
    try { await navigator.clipboard.writeText(url); toast.success('Link copied — send to attendees'); }
    catch { toast.error('Clipboard blocked'); }
  };

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.full_name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto' data-testid='admin-video-calls-page'>
      <div className='flex items-start justify-between mb-5 flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Video</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>Peer Video Calls</h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>Free unlimited video via Jitsi Meet. Call one client, a small group, or an entire class.</p>
        </div>
        <Button onClick={() => setOpen(true)} className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold' data-testid='add-call-btn'>
          <Plus className='w-4 h-4 mr-2' /> New call
        </Button>
      </div>

      {calls.length === 0 ? (
        <Card className='p-10 text-center text-slate-500 dark:text-slate-400'>
          <Video className='w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600' />
          No calls yet. Click “New call” to start a room.
        </Card>
      ) : (
        <div className='space-y-3'>
          {calls.map(c => (
            <Card key={c.id} className='p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 flex items-center gap-4 flex-wrap' data-testid={`call-row-${c.id}`}>
              <div className='w-10 h-10 rounded-lg bg-[#1B3A87] text-white flex items-center justify-center shrink-0'>
                {c.kind === 'group' ? <Users className='w-4 h-4' /> : <PhoneCall className='w-4 h-4' />}
              </div>
              <div className='flex-1 min-w-0'>
                <div className='font-semibold text-slate-900 dark:text-slate-50 truncate'>
                  {c.topic || (c.participant_names || []).join(', ') || 'Untitled call'}
                </div>
                <div className='text-xs text-slate-500 dark:text-slate-400'>
                  {(c.participant_names || []).length} participant{(c.participant_names||[]).length === 1 ? '' : 's'} &middot; {new Date(c.created_at).toLocaleString()}
                  {c.kind === 'group' && <Badge className='ml-2 bg-[#1B3A87] text-white text-[9px]'>Group</Badge>}
                </div>
              </div>
              <Button size='sm' variant='outline' onClick={() => copyLink(c)} data-testid={`call-copy-${c.id}`}><Copy className='w-3.5 h-3.5 mr-1' /> Copy link</Button>
              <Button size='sm' onClick={() => navigate(`/call/${c.id}`)} className='bg-[#C8102E] text-white' data-testid={`call-start-${c.id}`}>Start</Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-w-lg max-h-[85vh] overflow-y-auto' data-testid='add-call-dialog'>
          <DialogHeader><DialogTitle>Start a video call</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div><Label>Topic</Label><Input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder='Group training check-in' data-testid='nc-topic' /></div>
            <div><Label>Scheduled for (optional)</Label><Input type='datetime-local' value={form.scheduled_for} onChange={e => setForm({ ...form, scheduled_for: e.target.value })} data-testid='nc-sched' /></div>
            <div>
              <div className='flex items-center justify-between'>
                <Label>Clients ({form.participant_ids.length} selected)</Label>
                <div className='text-xs'>
                  <button onClick={selectAll} className='text-[#1B3A87] hover:underline mr-2' data-testid='select-all'>Select all</button>
                  <button onClick={selectNone} className='text-slate-500 hover:underline' data-testid='select-none'>Clear</button>
                </div>
              </div>
              <Input placeholder='Search…' value={search} onChange={e => setSearch(e.target.value)} className='mb-2' data-testid='nc-search' />
              <div className='max-h-64 overflow-y-auto border rounded divide-y divide-slate-100 dark:divide-slate-800'>
                {filtered.map(c => {
                  const sel = form.participant_ids.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-2 p-2 text-sm cursor-pointer ${sel ? 'bg-[#1B3A87]/5 dark:bg-[#1B3A87]/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`} data-testid={`client-pick-${c.id}`}>
                      <input type='checkbox' checked={sel} onChange={() => toggleClient(c.id)} className='accent-[#C8102E]' />
                      <span className='font-medium text-slate-900 dark:text-slate-50'>{c.full_name}</span>
                      <span className='text-xs text-slate-500 dark:text-slate-400 ml-auto'>{c.email}</span>
                    </label>
                  );
                })}
                {filtered.length === 0 && <div className='p-3 text-center text-slate-400 text-xs'>No clients match.</div>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createCall} className='bg-[#C8102E] text-white font-semibold' data-testid='nc-submit'>
              Create &amp; join
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
